import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Rocket, CheckCircle2, XCircle, Loader2, RefreshCw, Download, GitBranch,
  ShieldCheck, Clock, ExternalLink, AlertTriangle, PlugZap, Terminal,
  Github, Bell, Save, ChevronDown, ChevronRight, KeyRound, Copy, Check,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { getBrandName } from '@/lib/branding';

const STEP_LABELS = {
  queued: 'Queued', backup: 'Backing up', fetching: 'Fetching release',
  verifying: 'Verifying signature', building: 'Rebuilding', 'health-check': 'Health check',
  'rolling-back': 'Rolling back', done: 'Done', failed: 'Failed',
};

const WEEKDAYS = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];

const normVersion = (v) => String(v || '').trim().replace(/^v/i, '');
// returns 1 if a>b, -1 if a<b, 0 if equal; NaN if either is unparseable
const cmpVersion = (a, b) => {
  const pa = normVersion(a); const pb = normVersion(b);
  if (!/^\d+(\.\d+)*$/.test(pa) || !/^\d+(\.\d+)*$/.test(pb)) return NaN;
  const sa = pa.split('.').map((n) => parseInt(n, 10));
  const sb = pb.split('.').map((n) => parseInt(n, 10));
  const len = Math.max(sa.length, sb.length);
  for (let i = 0; i < len; i++) {
    const d = (sa[i] || 0) - (sb[i] || 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
};
// true once the running version has reached (or passed) the given target
const reachedTarget = (current, target) => {
  const c = cmpVersion(current, target);
  return !Number.isNaN(c) && c >= 0;
};

export default function SystemUpdatesPage() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);
  const [pendingTag, setPendingTag] = useState(null);
  const pollRef = useRef(null);
  const logBoxRef = useRef(null);
  const [logCopied, setLogCopied] = useState(false);

  const copyLog = async () => {
    const header = `${getBrandName()} update log — ${job?.target || ''} — ${job?.rolled_back ? 'FAILED (rolled back)' : (job?.success ? 'success' : 'failed')}`;
    const text = [header, job?.error ? `Error: ${job.error}` : null, '', ...(job?.log || [])]
      .filter((l) => l !== null)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setLogCopied(true);
      toast.success('Update log copied to clipboard');
      setTimeout(() => setLogCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the log text and copy manually');
    }
  };

  // config / notifications
  const [settings, setSettings] = useState(null);
  const [repo, setRepo] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [recipientsInput, setRecipientsInput] = useState('');
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  // trusted builds + scheduled window
  const [requireSigned, setRequireSigned] = useState(false);
  const [trustedKeys, setTrustedKeys] = useState('');
  const [autoInstall, setAutoInstall] = useState(false);
  const [approvedTag, setApprovedTag] = useState('');
  const [winDays, setWinDays] = useState([]);
  const [winStart, setWinStart] = useState('02:00');
  const [winMinutes, setWinMinutes] = useState(60);
  const [winTz, setWinTz] = useState('UTC');

  // versions browser
  const [releases, setReleases] = useState(null);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadStatus = async () => {
    try { setStatus(await api.getSystemUpdateStatus()); } catch (e) { toast.error(e.message); }
    finally { setLoadingStatus(false); }
  };
  const loadSettings = async () => {
    try {
      const s = await api.getUpdateSettings();
      setSettings(s);
      setRepo(s.github_repo || '');
      setAutoEnabled(!!s.enabled);
      setRecipientsInput((s.recipients || []).join(', '));
      setRequireSigned(!!s.require_signed);
      setTrustedKeys(s.trusted_gpg_keys || '');
      setAutoInstall(!!s.auto_install);
      setApprovedTag(s.approved_tag || '');
      setWinDays(s.window_days || []);
      setWinStart(s.window_start || '02:00');
      setWinMinutes(s.window_minutes || 60);
      setWinTz(s.window_tz || 'UTC');
    } catch (e) { /* non-super-admins won't reach here */ }
  };

  useEffect(() => { loadStatus(); loadSettings(); return () => clearInterval(pollRef.current); }, []);
  useEffect(() => {
    const box = logBoxRef.current;
    if (!box) return;
    // Only stick to the bottom when the user is already there — don't yank the
    // view (or the page) down while they're scrolled up reading earlier lines.
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 48;
    if (nearBottom) box.scrollTop = box.scrollHeight;
  }, [job?.log]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        enabled: autoEnabled,
        recipients: recipientsInput.split(',').map((x) => x.trim()).filter(Boolean),
        github_repo: repo.trim(),
        require_signed: requireSigned,
        trusted_gpg_keys: trustedKeys,
        auto_install: autoInstall,
        window_days: winDays,
        window_start: winStart,
        window_minutes: Number(winMinutes) || 60,
        window_tz: winTz.trim() || 'UTC',
      };
      if (tokenInput.trim()) payload.github_token = tokenInput.trim();
      const s = await api.saveUpdateSettings(payload);
      setSettings(s);
      setTokenInput('');
      toast.success('Settings saved.');
      loadStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSavingCfg(false); }
  };

  const clearToken = async () => {
    setSavingCfg(true);
    try {
      const s = await api.saveUpdateSettings({ github_token: '__CLEAR__' });
      setSettings(s); setTokenInput('');
      toast.success('Token removed.'); loadStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSavingCfg(false); }
  };

  const approveTag = async (tag) => {
    try {
      const s = await api.saveUpdateSettings({ approved_tag: tag || '' });
      setSettings(s); setApprovedTag(s.approved_tag || '');
      toast.success(tag ? `Approved ${tag} for auto-install` : 'Approval cleared');
    } catch (e) { toast.error(e.message); }
  };

  const toggleDay = (d) => setWinDays((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d].sort((a, b) => a - b)));

  const runCheck = async () => {
    setChecking(true); setCheckResult(null);
    try {
      const r = await api.checkForUpdates();
      setCheckResult(r);
      r.update_available ? toast.success(`Update available: ${r.latest}`) : toast.success('You are up to date.');
      loadSettings();
    } catch (e) { toast.error(e.message); }
    finally { setChecking(false); }
  };

  const loadReleases = async () => {
    setLoadingReleases(true);
    try { setReleases(await api.getReleases()); }
    catch (e) { toast.error(e.message); }
    finally { setLoadingReleases(false); }
  };

  const pollResult = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.getUpdateResult(jobId);
        setJob(r);
        if (r.done) {
          clearInterval(pollRef.current); setRunning(false);
          r.success ? toast.success('Update completed successfully.') : toast.error('Update failed — the server was rolled back.');
          loadStatus(); loadSettings();
        }
      } catch (e) {
        setJob((prev) => prev ? { ...prev, log: [...(prev.log || []), `· waiting for server… (${e.message})`] } : prev);
      }
    }, 2000);
  };

  const startUpdate = async (tag) => {
    setPendingTag(null); setRunning(true);
    setJob({ state: 'queued', log: ['Requesting update…'], done: false });
    try {
      const r = await api.runSystemUpdate(tag || null);
      if (!r.job_id) throw new Error('Updater did not return a job id.');
      pollResult(r.job_id);
    } catch (e) { setRunning(false); setJob(null); toast.error(e.message); }
  };

  const updater = status?.updater;
  const canUpdate = !!updater?.reachable;
  // Once the running version has reached the target, a prior "update available" result is stale — hide it.
  const checkStale = !!(checkResult?.update_available && reachedTarget(status?.current_version, checkResult?.latest));
  const settingsUpdateStale = !!(settings?.update_available && reachedTarget(status?.current_version, settings?.last_latest));

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="system-updates-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Rocket className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">System Updates</h1>
          <p className="text-sm text-muted-foreground">Check for a newer {getBrandName()} release and update this server in place. A backup is taken first and the server automatically rolls back if the new version is unhealthy.</p>
        </div>
      </div>

      {/* Version / status card */}
      <Card className="p-5 space-y-5" data-testid="update-status-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <GitBranch className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Running version</h2>
              <p className="text-sm text-muted-foreground" data-testid="current-version">
                {loadingStatus ? 'Loading…' : (
                  <>
                    <strong className="text-foreground">{status?.current_version || 'unknown'}</strong>
                    {status?.build_date ? <> · built {status.build_date}</> : null}
                    {status?.commit ? <> · {status.commit}</> : null}
                  </>
                )}
              </p>
            </div>
          </div>
          {!loadingStatus && (
            <Badge variant="outline" className={canUpdate ? 'border-emerald-500/40 text-emerald-500' : 'border-amber-500/40 text-amber-500'} data-testid="updater-badge">
              {canUpdate ? 'Updater ready' : 'Updater offline'}
            </Badge>
          )}
        </div>

        {!loadingStatus && !canUpdate && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground flex items-start gap-2" data-testid="updater-offline-note">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span>The updater component isn’t reachable ({updater?.detail}). On the Emergent-hosted preview this is expected — deploy with the <strong>Publish</strong> button. On a self-hosted Docker Compose install, ensure the <code>updater</code> service is running.</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button onClick={runCheck} disabled={checking || running} variant="outline" data-testid="check-updates-btn">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {checking ? 'Checking…' : 'Check for updates'}
          </Button>
          <span className="text-xs text-muted-foreground">
            Repo {status?.repo_configured ? 'configured' : 'not configured'} · Token {status?.token_configured ? 'set' : 'not set'}
            {settings?.last_checked_at ? <> · last checked {new Date(settings.last_checked_at).toLocaleString()}</> : null}
          </span>
        </div>

        {checkResult && !checkStale && (
          <div className={`rounded-lg border p-4 ${checkResult.update_available ? 'border-primary/30 bg-primary/5' : 'border-emerald-500/30 bg-emerald-500/5'}`} data-testid="check-result">
            <div className="flex items-start gap-3">
              {checkResult.update_available ? <Download className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />}
              <div className="space-y-1 flex-1 min-w-0">
                <div className="font-medium">{checkResult.update_available ? `Update available — ${checkResult.current} → ${checkResult.latest}` : `Up to date (${checkResult.current})`}</div>
                {checkResult.release?.name && <div className="text-sm text-muted-foreground truncate">{checkResult.release.name}</div>}
                {checkResult.release?.published_at && <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Published {new Date(checkResult.release.published_at).toLocaleDateString()}</div>}
                {checkResult.release?.notes_url && <a href={checkResult.release.notes_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="release-notes-link">Release notes <ExternalLink className="w-3 h-3" /></a>}
              </div>
            </div>
            {checkResult.update_available && !running && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button onClick={() => setPendingTag(checkResult.latest)} disabled={!canUpdate} data-testid="update-now-btn">
                  <Download className="w-4 h-4 mr-2" /> Update now
                </Button>
                {approvedTag === checkResult.latest ? (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved for auto-install</Badge>
                ) : (
                  <Button variant="outline" onClick={() => approveTag(checkResult.latest)} data-testid="approve-latest-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Approve for auto-install
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Confirm panel (shared by "Update now" and "Install this version") */}
      {pendingTag && !running && (
        <Card className="p-4 border-primary/40 space-y-3" data-testid="update-confirm">
          <div className="text-sm flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span>This backs up the database, rebuilds and restarts the server to <strong>{pendingTag}</strong>, and automatically rolls back if it doesn’t come up healthy. The app may be briefly unavailable.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => startUpdate(pendingTag)} data-testid="confirm-update-btn"><Rocket className="w-4 h-4 mr-2" /> Yes, update to {pendingTag}</Button>
            <Button variant="ghost" onClick={() => setPendingTag(null)} data-testid="cancel-update-btn">Cancel</Button>
          </div>
        </Card>
      )}

      {/* Live progress */}
      {job && (
        <Card className="p-5 space-y-4" data-testid="update-progress-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {job.done ? (job.success ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-red-500" />) : <Loader2 className="w-5 h-5 text-primary animate-spin" />}
              <span className="font-medium" data-testid="update-state">
                {job.done ? (job.success ? `Updated to ${job.target || ''}` : (job.rolled_back ? 'Update failed — rolled back' : 'Update failed')) : (STEP_LABELS[job.state] || job.state || 'Working…')}
              </span>
            </div>
            {!job.done && <Badge variant="outline" className="border-primary/40 text-primary"><PlugZap className="w-3 h-3 mr-1" /> In progress</Badge>}
          </div>
          {job.error && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400" data-testid="update-error">{job.error}</div>}
          <div ref={logBoxRef} className="rounded-lg bg-black/40 border border-border/50 p-3 max-h-80 overflow-auto font-mono text-[11px] leading-relaxed text-muted-foreground" data-testid="update-log">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground/60"><Terminal className="w-3 h-3" /> Update log</div>
              {job.done && !job.success && (job.log || []).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[10px] gap-1"
                  onClick={copyLog}
                  data-testid="copy-update-log-btn"
                >
                  {logCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                  {logCopied ? 'Copied' : 'Copy log'}
                </Button>
              )}
            </div>
            {(job.log || []).map((l, i) => <div key={i} className="whitespace-pre-wrap break-words">{l}</div>)}
          </div>
        </Card>
      )}

      {/* Available versions */}
      <Card className="p-5 space-y-4" data-testid="versions-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Download className="w-4 h-4 text-primary" /><h2 className="text-base font-semibold">Available versions</h2></div>
          <Button variant="outline" size="sm" onClick={loadReleases} disabled={loadingReleases} data-testid="browse-versions-btn">
            {loadingReleases ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {releases ? 'Refresh' : 'Browse versions'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Install a specific published release tag instead of only the latest. Read the notes, then install.</p>
        {releases && (
          <div className="space-y-2" data-testid="releases-list">
            {releases.releases.length === 0 && <div className="text-sm text-muted-foreground">No published releases found.</div>}
            {releases.releases.map((r) => (
              <div key={r.tag} className="rounded-lg border border-border/60 p-3" data-testid={`release-${r.tag}`}>
                <div className="flex items-center justify-between gap-3">
                  <button className="flex items-center gap-2 text-left min-w-0" onClick={() => setExpanded((e) => ({ ...e, [r.tag]: !e[r.tag] }))}>
                    {expanded[r.tag] ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        {r.tag}
                        {r.is_current && <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px]">current</Badge>}
                        {r.prerelease && <Badge variant="outline" className="border-amber-500/40 text-amber-500 text-[10px]">pre-release</Badge>}
                      </div>
                      {r.name && <div className="text-xs text-muted-foreground truncate">{r.name}</div>}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.published_at && <span className="text-[11px] text-muted-foreground hidden sm:block">{new Date(r.published_at).toLocaleDateString()}</span>}
                    {r.is_current ? (
                      <span className="text-xs text-muted-foreground">Installed</span>
                    ) : (
                      <>
                        {approvedTag === r.tag ? (
                          <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px]">approved</Badge>
                        ) : r.installable && (
                          <Button size="sm" variant="ghost" className="h-8 px-2" title="Approve for auto-install" onClick={() => approveTag(r.tag)} data-testid={`approve-${r.tag}`}>
                            <ShieldCheck className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" disabled={!canUpdate || running} onClick={() => setPendingTag(r.tag)} data-testid={`install-${r.tag}`}>Install</Button>
                      </>
                    )}
                  </div>
                </div>
                {expanded[r.tag] && (
                  <div className="mt-3 pl-6 space-y-2">
                    {r.notes_url && <a href={r.notes_url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">Open on GitHub <ExternalLink className="w-3 h-3" /></a>}
                    <pre className="text-[11px] whitespace-pre-wrap break-words text-muted-foreground bg-muted/30 rounded-md p-3 max-h-56 overflow-auto">{r.body || 'No release notes.'}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* GitHub configuration */}
      <Card className="p-5 space-y-4" data-testid="github-config-card">
        <div className="flex items-center gap-2"><Github className="w-4 h-4" /><h2 className="text-base font-semibold">GitHub configuration</h2></div>
        <p className="text-xs text-muted-foreground">Point updates at your release repository. The token is stored encrypted and never shown again.{status?.config_source === 'env' ? ' Currently using values from the server environment.' : ''}</p>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="gh-repo" className="text-xs">Repository (owner/repo)</Label>
            <Input id="gh-repo" value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="my-org/inzara" data-testid="github-repo-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gh-token" className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> Personal Access Token {settings?.github_token_set && <Badge variant="outline" className="border-emerald-500/40 text-emerald-500 text-[10px] ml-1">set</Badge>}</Label>
            <Input id="gh-token" type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder={settings?.github_token_set ? '•••••••• (leave blank to keep)' : 'ghp_… (read access to code + releases)'} data-testid="github-token-input" />
            {settings?.github_token_set && <button className="text-[11px] text-red-400 hover:underline" onClick={clearToken} data-testid="clear-token-btn">Remove stored token</button>}
          </div>
        </div>
      </Card>

      {/* Notifications */}
      <Card className="p-5 space-y-4" data-testid="notifications-card">
        <div className="flex items-center gap-2"><Bell className="w-4 h-4" /><h2 className="text-base font-semibold">Update notifications</h2></div>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium">Email me when a new version is available</div>
            <p className="text-xs text-muted-foreground">A daily check notifies the recipients below the first time each new release appears.</p>
          </div>
          <Switch checked={autoEnabled} onCheckedChange={setAutoEnabled} data-testid="auto-check-switch" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="recips" className="text-xs">Recipients (comma-separated — leave blank to notify all admins)</Label>
          <Input id="recips" value={recipientsInput} onChange={(e) => setRecipientsInput(e.target.value)} placeholder="ops@acme.com, admin@acme.com" data-testid="recipients-input" />
        </div>
        {settings?.update_available && !settingsUpdateStale && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5 text-xs text-primary flex items-center gap-2" data-testid="update-available-note">
            <Download className="w-3.5 h-3.5" /> A newer release ({settings.last_latest}) is available.
          </div>
        )}
      </Card>

      {/* Trusted builds (signed releases) */}
      <Card className="p-5 space-y-4" data-testid="trusted-builds-card">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /><h2 className="text-base font-semibold">Trusted builds</h2></div>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium">Only install signed releases</div>
            <p className="text-xs text-muted-foreground">Before building, the server verifies the release tag’s GPG signature against your trusted keys and aborts if it isn’t trusted.</p>
          </div>
          <Switch checked={requireSigned} onCheckedChange={setRequireSigned} data-testid="require-signed-switch" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="keys" className="text-xs">Trusted signing public keys (ASCII-armored, one or more)</Label>
          <Textarea id="keys" rows={5} value={trustedKeys} onChange={(e) => setTrustedKeys(e.target.value)} placeholder={'-----BEGIN PGP PUBLIC KEY BLOCK-----\n…\n-----END PGP PUBLIC KEY BLOCK-----'} className="font-mono text-[11px]" data-testid="trusted-keys-input" />
          {requireSigned && !trustedKeys.trim() && (
            <p className="text-[11px] text-amber-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Add at least one public key, or installs will be blocked.</p>
          )}
        </div>
      </Card>

      {/* Scheduled window (auto-install approved updates) */}
      <Card className="p-5 space-y-4" data-testid="scheduled-window-card">
        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /><h2 className="text-base font-semibold">Scheduled window</h2></div>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium">Auto-install approved updates</div>
            <p className="text-xs text-muted-foreground">When you approve a version, it installs automatically during the next maintenance window — no manual click needed.</p>
          </div>
          <Switch checked={autoInstall} onCheckedChange={setAutoInstall} data-testid="auto-install-switch" />
        </div>
        <div className="rounded-lg border border-border/60 bg-muted/20 p-2.5 text-xs flex items-center gap-2" data-testid="approved-tag-row">
          {approvedTag ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              <span>Approved and waiting: <strong>{approvedTag}</strong></span>
              <button className="text-red-400 hover:underline ml-auto" onClick={() => approveTag('')} data-testid="clear-approval-btn">Clear</button>
            </>
          ) : (
            <span className="text-muted-foreground">No version approved yet — use “Approve for auto-install” on a version above.</span>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Days</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="window-days">
            {WEEKDAYS.map(([lbl, d]) => (
              <button key={d} onClick={() => toggleDay(d)} data-testid={`day-${d}`}
                className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${winDays.includes(d) ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Leave all unselected for every day.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="win-start" className="text-xs">Start (24h)</Label>
            <Input id="win-start" value={winStart} onChange={(e) => setWinStart(e.target.value)} placeholder="02:00" data-testid="window-start-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="win-min" className="text-xs">Duration (min)</Label>
            <Input id="win-min" type="number" value={winMinutes} onChange={(e) => setWinMinutes(e.target.value)} data-testid="window-minutes-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="win-tz" className="text-xs">Timezone</Label>
            <Input id="win-tz" value={winTz} onChange={(e) => setWinTz(e.target.value)} placeholder="UTC" data-testid="window-tz-input" />
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={saveConfig} disabled={savingCfg} data-testid="save-config-btn">
          {savingCfg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save settings
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">Updates install published <strong>release tags</strong> from your GitHub repository and apply only to self-hosted Docker Compose installs.</p>
    </div>
  );
}
